import React from 'react'
import { Settings2, Paintbrush, Code, Accessibility } from 'lucide-react'
import type { RightPanelMode, CmsEditorRightPanelProps } from './types'
import PropertyInspector from '@/components/cms/PropertyInspector'
import BlockStyleEditor from '@/components/cms/BlockStyleEditor'
import CodeInspector from '@/components/cms/CodeInspector'
import A11yChecker from '@/components/cms/A11yChecker'

/* ── Tab definitions ─────────────────────────────────── */

const RIGHT_TABS: { mode: RightPanelMode; icon: React.ReactNode; label: string }[] = [
  { mode: 'properties', icon: <Settings2 size={13} />, label: 'Props' },
  { mode: 'style', icon: <Paintbrush size={13} />, label: 'Style' },
  { mode: 'code', icon: <Code size={13} />, label: 'Code' },
  { mode: 'a11y', icon: <Accessibility size={13} />, label: 'A11y' },
]

/* ── Component ───────────────────────────────────────── */

export default function CmsEditorRightPanel({
  rightPanelMode,
  rightPanelWidth,
  selectedBlock,
  onSetRightPanelMode,
  onUpdateBlock,
  onUpdateBlockStyles,
  onResizeBlock,
  onRemoveBlock,
  onDuplicateBlock,
  onResizeStart,
}: CmsEditorRightPanelProps) {
  return (
    <aside
      className="editor-panel"
      style={{
        width: rightPanelWidth,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: '1px solid var(--editor-border)',
        position: 'relative',
      }}
    >
      {/* Tab bar */}
      <div className="editor-tab-bar">
        {RIGHT_TABS.map(({ mode, icon, label }) => (
          <button
            key={mode}
            className="editor-tab"
            data-active={rightPanelMode === mode}
            onClick={() => onSetRightPanelMode(mode)}
            title={label}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {rightPanelMode === 'properties' && (
          <PropertyInspector
            block={selectedBlock}
            onUpdateBlock={onUpdateBlock}
            onResizeBlock={onResizeBlock}
            onRemoveBlock={onRemoveBlock}
            onDuplicateBlock={onDuplicateBlock}
          />
        )}

        {rightPanelMode === 'style' && (
          <BlockStyleEditor
            block={selectedBlock}
            onUpdateStyles={(blockId, styles) => onUpdateBlockStyles(blockId, styles)}
          />
        )}

        {rightPanelMode === 'code' && (
          <CodeInspector block={selectedBlock} />
        )}

        {rightPanelMode === 'a11y' && (
          <A11yChecker
            block={selectedBlock}
            onGoToProperty={(section) => {
              if (section === 'content') onSetRightPanelMode('properties')
              else if (section === 'style') onSetRightPanelMode('style')
            }}
          />
        )}

        {/* Empty state when no block selected */}
        {!selectedBlock && rightPanelMode !== 'code' && rightPanelMode !== 'a11y' && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--editor-text-dim)',
            fontSize: 'var(--editor-font-size-sm)',
            gap: 8,
            padding: 24,
            textAlign: 'center',
          }}>
            <span style={{ fontSize: 20, opacity: 0.3 }}>⬜</span>
            <span>Select a block to inspect</span>
          </div>
        )}
      </div>

      {/* Resize handle */}
      <div
        className="editor-split-handle"
        onMouseDown={onResizeStart}
        style={{ position: 'absolute', left: 0, top: 0, bottom: 0 }}
      />
    </aside>
  )
}
